import createClient, { type Middleware } from 'openapi-fetch';
import type { components, paths } from './__generated__/samera-one';
import { env } from '~shared/config/env';
import { isLocal, isStaging } from '~shared/config/hostname';
import { normalizeValidationErrors } from './api.lib';

/**
 * The backend's error body. Every samera-one operation now declares a `4XX`/`5XX` → ApiError
 * response in the OpenAPI contract (samera/samera#3516, generated from
 * RestExceptionHandler.ErrorsDTO), so this is a type alias, not a hand-written shape.
 */
export type ApiErrorDataDto = components['schemas']['ApiError'];

/** The normalized, localized, user-facing messages derived from the body above. */
export type ApiErrorData = string[];

/**
 * A generated type is not a runtime guarantee — the body on the wire is still whatever the
 * server actually sent, e.g. an ALB/CloudFront 502 page or a filter-level rejection that never
 * reaches RestExceptionHandler. This narrows on the one thing every caller reads: `errors` is
 * an array. See {@link unwrap}, which decides on the result of that narrowing, not on the shape
 * alone (samera/samera#3516; previously a Zod `safeParse` did the same narrowing).
 */
function isApiErrorDataDto(error: unknown): error is ApiErrorDataDto {
  return (
    !!error &&
    typeof error === 'object' &&
    Array.isArray((error as { errors?: unknown }).errors)
  );
}

/**
 * Typed samera-one API client, generated from the committed OpenAPI contract
 * (openapi/samera-one.json → src/shared/api/__generated__/samera-one.ts,
 * regenerated via `npm run generate:api`). openapi-fetch gives fully-typed
 * paths, params and responses.
 *
 * Unlike samera-cloud, the base URL is the **origin only**: the generated path
 * keys carry the full path including `/rest/v1/samera-one/{oneIdentifier}`, and
 * `oneIdentifier` is a path param on every operation — so it is passed per call
 * (params.path.oneIdentifier), not baked into the base URL.
 *
 * Every samera-one endpoint goes through this client (samera/samera#3319).
 */

/**
 * Resolve the backend **origin** (scheme + host [+ port]) for the current
 * environment. `env.API_URL` / `env.STAGING_API_URL` include the
 * `/rest/v1/samera-one` path tail; we strip it here via `URL.origin` because openapi-fetch
 * concatenates baseUrl + the full generated path. Local dev uses the worktree-aware `VITE_BACKEND_URL` origin.
 */
export function getApiOrigin(): string {
  const raw = isLocal
    ? import.meta.env.VITE_BACKEND_URL ?? 'http://localhost:18080'
    : isStaging
    ? env.STAGING_API_URL ?? env.API_URL
    : env.API_URL;
  return new URL(raw).origin;
}

export const API_ORIGIN = getApiOrigin();

/**
 * Error thrown by {@link unwrap} on a non-2xx response. `messages` carries the
 * localized, user-facing strings produced by {@link normalizeValidationErrors}
 * — the same shape the axios response interceptor puts on `error.response.data`,
 * so a shared accessor can read either transport during the migration.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly messages: string[];
  /**
   * Request URL, carried so the error reporter can tag a typed-client failure
   * the way it tags an AxiosError (`observability/api-error-enrichment.ts`).
   * Without it a 500 from `configuration` and one from `last-booking` are
   * indistinguishable in the record — a gap that would grow with every migrated
   * endpoint. The method is not carried: `Response` does not expose it, and
   * threading it through every call site would be boilerplate the URL already
   * makes redundant.
   */
  readonly url?: string;

  constructor(status: number, messages: string[], request?: { url?: string }) {
    super(messages[0] ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.messages = messages;
    this.url = request?.url;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

/**
 * The localized, user-facing error messages carried by an {@link ApiError}, or `undefined` for
 * any other error shape. Use this instead of reading the error body at a call site.
 */
export function getApiErrorMessages(error: unknown): string[] | undefined {
  return isApiError(error) ? error.messages : undefined;
}

const SESSION_HEADER = 'X-Samera-Session-Id';

// Injected from app.tsx — keeps this module free of a store→api circular import and makes the
// middleware unit-testable.
let getSessionId: () => string | undefined = () => undefined;
let onStaleSession: () => void = () => {};

export function configureApiClient(config: {
  getSessionId: () => string | undefined;
  onStaleSession: () => void;
}): void {
  getSessionId = config.getSessionId;
  onStaleSession = config.onStaleSession;
}

const sessionMiddleware: Middleware = {
  onRequest({ request }) {
    const sessionId = getSessionId();
    if (sessionId) request.headers.set(SESSION_HEADER, sessionId);
    return request;
  },
  async onResponse({ request, response }) {
    // Stale-session reset: fire ONLY when this request carried a session id AND
    // the backend replied with the `no_session` error code. Matched on the error
    // *body* code, not the HTTP status — a 404 from /session/last-booking is
    // legitimate and must not trigger a reset (the documented cascade scar).
    // `response.clone()` so reading the body here doesn't consume the single-use
    // stream openapi-fetch parses downstream.
    //
    // The id must still be the CURRENT one: responses can land out of order, so
    // a slow reply for an already-replaced session would otherwise wipe the
    // fresh one the guest is now using.
    const sentSessionId = request.headers.get(SESSION_HEADER);
    if (!response.ok && sentSessionId && sentSessionId === getSessionId()) {
      const body = await response
        .clone()
        .json()
        .catch(() => null);
      const hasNoSession =
        !!body &&
        Array.isArray((body as { errors?: unknown }).errors) &&
        (body as { errors: Array<{ error?: string }> }).errors.some(
          e => e?.error === 'no_session',
        );
      if (hasNoSession) onStaleSession();
    }
    return response;
  },
};

export const apiClient = createClient<paths>({
  baseUrl: API_ORIGIN,
  // Defer to the global fetch at call time rather than binding it at client
  // creation, so tests can stub it (and any future fetch wrapper slots in here).
  fetch: (...args: Parameters<typeof globalThis.fetch>) =>
    globalThis.fetch(...args),
});
apiClient.use(sessionMiddleware);

/**
 * Bridge openapi-fetch's `{ data, error, response }` result to the throw-based
 * contract the app (and TanStack Query) expect: on a non-2xx response, throw an
 * {@link ApiError} carrying the normalized localized messages (reusing
 * {@link normalizeValidationErrors}, so both transports produce the same
 * `string[]`); otherwise return the typed `data`. A body-less 2xx (e.g. 204)
 * resolves to `undefined`.
 */
export async function unwrap<T>(
  call: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await call;
  if (!response.ok || error !== undefined) {
    // Narrowing successfully is not the same as learning anything: `errors: []`
    // narrows fine and yields zero messages, so `{errors: [], message: "Hotel
    // disabled"}` would throw away the only usable text. Decide on the RESULT,
    // not on whether the shape matched.
    const normalized = isApiErrorDataDto(error)
      ? normalizeValidationErrors(error).filter(message => message !== '')
      : [];
    const messages = normalized.length
      ? normalized
      : [fallbackMessage(error, response)];
    throw new ApiError(response.status, messages, {
      url: response.url || undefined,
    });
  }
  return data as T;
}

/**
 * Best available message when the error body does not match the backend's
 * validation-error schema — a proxy/CDN page, an infrastructure error, an
 * unhandled exception shape. Mirrors what the axios interceptor already
 * salvages (`api.instance.ts`); without it the cause is thrown away and both
 * the UI and the error reporter only ever see "Bad Request".
 */
function fallbackMessage(error: unknown, response: Response): string {
  const statusText =
    response.statusText || `Request failed (${response.status})`;
  if (typeof error === 'string' && error.length > 0) return error;
  if (error && typeof error === 'object') {
    const { message, error: errorField } = error as Record<string, unknown>;
    if (typeof message === 'string' && message.length > 0) return message;
    if (typeof errorField === 'string' && errorField.length > 0)
      return errorField;
  }
  return statusText;
}
